import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  agentService,
  kycService,
  getAgencyDashboardOverview,
  getAgencyDashboardRecentActivity,
  getAgencyTopAgents,
  getAgencyPitches,
  userService,
} from "../services/api";
import toast from "react-hot-toast";
import { updateFromProfile } from "../store/slices/authSlice";
import { ROLES } from "../config/roles";

const kycFlag = process.env.REACT_APP_KYC_ENABLE_MODE === "TRUE";

const useHomeDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, kycVerified, currentRole } = useSelector((state) => state.auth);

  // Agent states
  const [recentActivity, setRecentActivity] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);

  // Agency states
  const [agencyOverview, setAgencyOverview] = useState(null);
  const [agencyActivities, setAgencyActivities] = useState([]);
  const [agencyTopAgents, setAgencyTopAgents] = useState([]);
  const [agencyPitches, setAgencyPitches] = useState([]);
  const [agencyLoading, setAgencyLoading] = useState(false);

  // KYC states
  const [kycLoading, setKycLoading] = useState(false);

  // Ref to track if profile sync has been done
  const hasProfileSynced = useRef(false);

  // Sync profile data
  const syncProfileAccessData = useCallback(async () => {
    if (hasProfileSynced.current) return;
    
    const arraysEqualUnordered = (a = [], b = []) => {
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      const mapCount = (arr) =>
        arr.reduce((acc, v) => {
          acc[v] = (acc[v] || 0) + 1;
          return acc;
        }, {});
      const ac = mapCount(a);
      const bc = mapCount(b);
      const keys = Object.keys(ac);
      if (keys.length !== Object.keys(bc).length) return false;
      for (const k of keys) {
        if (ac[k] !== bc[k]) return false;
      }
      return true;
    };

    try {
      const res = await userService.getProfile();
      if (!res.success) return;
      const profile = res.data || {};
      console.log("Fetched profile for sync:", profile);
      const freshPermissions =
        profile.permissions || profile?.data?.permissions || [];
      const freshGroups = profile.groups || profile?.data?.groups || [];
      const freshRoles = profile.roles || profile?.data?.roles || [];
      const freshKycVerified = kycFlag ? profile.kyc_verified : true;

      console.log("freshKycVerified", freshKycVerified);

      // Display names (T012): sessions logged in before names were stored at
      // auth time miss them in state.auth.user — backfill from the same
      // profile fetch so attribution lines show real names, not fallbacks.
      const freshName =
        profile.name || profile?.data?.name || profile?.user_details?.name || "";
      const freshClubName =
        profile.club_details?.name || profile?.data?.club_details?.name || "";
      const freshAgencyName =
        profile.agency_details?.name || profile?.data?.agency_details?.name || "";

      const storedPermissions = user?.permissions || [];
      const storedGroups = user?.groups || [];
      const storedRoles = user?.roles || [];
      const storedKycVerified = user?.kycVerified || false;

      const hasPermChanged = !arraysEqualUnordered(
        storedPermissions,
        freshPermissions
      );
      const hasGroupsChanged = !arraysEqualUnordered(
        storedGroups,
        freshGroups
      );
      const hasRolesChanged = !arraysEqualUnordered(storedRoles, freshRoles);
      const hasKycChanged = storedKycVerified !== freshKycVerified;
      const hasNamesChanged =
        (freshName && freshName !== user?.name) ||
        (freshClubName && freshClubName !== user?.club_name) ||
        (freshAgencyName && freshAgencyName !== user?.agency_name);

      if (
        hasPermChanged ||
        hasGroupsChanged ||
        hasRolesChanged ||
        hasKycChanged ||
        hasNamesChanged
      ) {
        dispatch(
          updateFromProfile({
            permissions: freshPermissions,
            groups: freshGroups,
            roles: freshRoles,
            kycVerified: freshKycVerified,
            name: freshName,
            club_name: freshClubName,
            agency_name: freshAgencyName,
          })
        );
      }
      
      hasProfileSynced.current = true;
    } catch (error) {
      console.error("Error syncing profile access data:", error);
    }
  }, [dispatch, user]);

  // Fetch agent recent activity
  const fetchAgentRecentActivity = useCallback(async () => {
    setRecentLoading(true);
    try {
      const response = await agentService.getRecentActivity();
      if (response.success) {
        console.log("Recent Activity", response.data);
        setRecentActivity(response.data);
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      console.error("Error fetching agent recent activity:", error);
    } finally {
      setRecentLoading(false);
    }
  }, []);

  // Fetch agency dashboard data
  const fetchAgencyDashboardData = useCallback(async () => {
    setAgencyLoading(true);
    try {
      const [overviewRes, activitiesRes, topAgentsRes, pitchesRes] =
        await Promise.all([
          getAgencyDashboardOverview(),
          getAgencyDashboardRecentActivity(10),
          getAgencyTopAgents(3),
          getAgencyPitches({ limit: 5, page: 1 }),
        ]);

      if (overviewRes.data && overviewRes.data.data) {
        setAgencyOverview(overviewRes.data.data);
      }

      if (activitiesRes.data && activitiesRes.data.data) {
        setAgencyActivities(activitiesRes.data.data);
      }

      if (topAgentsRes.data && topAgentsRes.data.data) {
        setAgencyTopAgents(topAgentsRes.data.data);
      }

      if (
        pitchesRes.data &&
        pitchesRes.data.results &&
        pitchesRes.data.results.data
      ) {
        setAgencyPitches(pitchesRes.data.results.data);
      }
    } catch (error) {
      console.error("Error fetching agency dashboard data:", error);
    } finally {
      setAgencyLoading(false);
    }
  }, []);

  // Handle KYC verification
  const handleVerifyAccount = useCallback(async () => {
    setKycLoading(true);
    try {
      const res = await kycService.getVerificationLink();
      if (res?.success && res?.data?.url) {
        navigate("/kyc-verification", { state: { documentUrl: res.data.url } });
      } else {
        toast.error(res?.error || "Failed to get verification link");
      }
    } catch (error) {
      toast.error("Failed to get verification link");
    } finally {
      setKycLoading(false);
    }
  }, [navigate]);

  // Navigation handlers
  const handleNavigateToPlayer = useCallback(
    (playerId) => {
      navigate(`/search/player/${playerId}`);
    },
    [navigate]
  );

  const handleNavigateToClub = useCallback(
    (clubId) => {
      navigate(`/search/club/${clubId}`);
    },
    [navigate]
  );

  const handleNavigateToAgent = useCallback(
    (agentId) => {
      navigate(`/search/agent/${agentId}`);
    },
    [navigate]
  );

  // Initial profile sync - only run once on mount
  useEffect(() => {
    syncProfileAccessData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch data based on role
  useEffect(() => {
    if (currentRole === ROLES.Agent) {
      fetchAgentRecentActivity();
    } else if (currentRole === ROLES.AgencyAdmin) {
      fetchAgencyDashboardData();
    }
  }, [currentRole, fetchAgentRecentActivity, fetchAgencyDashboardData]);

  return {
    // User data
    user,
    kycVerified,
    currentRole,

    // Agent data
    recentActivity,
    recentLoading,

    // Agency data
    agencyOverview,
    agencyActivities,
    agencyTopAgents,
    agencyPitches,
    agencyLoading,

    // KYC
    kycLoading,
    handleVerifyAccount,

    // Navigation handlers
    handleNavigateToPlayer,
    handleNavigateToClub,
    handleNavigateToAgent,
  };
};

export default useHomeDashboard;
